import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;

if (!AUTH0_DOMAIN) {
  throw new Error('Missing VITE_AUTH0_DOMAIN in environment variables');
}

if (!AUTH0_CLIENT_ID) {
  throw new Error('Missing VITE_AUTH0_CLIENT_ID in environment variables');
}

interface Auth0ProviderWithRoutesProps {
  children: React.ReactNode;
}

/**
 * Auth0Provider wrapper
 *
 * Configures Auth0 integration with React Router:
 * - Uses navigate for routing after authentication
 * - Configures redirect paths after sign-in/sign-up
 * - Uses Universal Login (redirect to Auth0 hosted page)
 *
 * Environment variables:
 * - VITE_AUTH0_DOMAIN: Auth0 tenant domain (e.g., studyjoyful.jp.auth0.com)
 * - VITE_AUTH0_CLIENT_ID: Auth0 SPA application client ID
 * - VITE_AUTH0_AUDIENCE: Auth0 API identifier (e.g., https://api.studyjoyful.com)
 */
export function Auth0ProviderWithRoutes({
  children,
}: Auth0ProviderWithRoutesProps) {
  const navigate = useNavigate();

  /**
   * Handle redirect callback after authentication
   * Auth0 redirects back to the app with auth result in URL
   */
  const onRedirectCallback = (appState?: AppState) => {
    // Navigate to the returnTo path or home
    navigate(appState?.returnTo || '/', { replace: true });
  };

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
    >
      {children}
    </Auth0Provider>
  );
}

